import StickyNavbar from '../navbar';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import QuizPopup from "./QuizPopup";
import React, { useState } from "react";

const Chapter3 = () => {
  const [showQuiz, setShowQuiz] = useState(false);
  
    const handleQuizComplete = (answers) => {
      console.log("User answers:", answers);
      // You can process the answers here
    };
    
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const hoverEffect = {
    whileHover: { scale: 1.05 },
    transition: { duration: 0.3 },
  };

  // Scroll animation on visibility
  const { ref: observationRef, inView: inViewObservational } = useInView({ triggerOnce: true, threshold: 0.2 });
  const { ref: experimentalRef, inView: inViewExperimental } = useInView({ triggerOnce: true, threshold: 0.2 });
  const { ref: interventionRef, inView: inViewIntervention } = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div>
      <StickyNavbar />
      <motion.div
        className="bg-white text-black p-4 lg:mt-10 mt-20 sm:p-10 lg:px-20 text-lg"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <motion.div className="text-center mb-8" variants={fadeIn}>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold capitalize">
            Chapter 3: Different types of research
          </h1>
          <p className='text-[#005FFF] text-2xl py-5'> Intended Behavioural Outcome (IBO)</p>
          <p>
            The Community Health Extension Worker (WHO) will choose the best type of research to do (WHAT) with members of the community (WITH WHOM) by understanding observations, experiments, and interventions and the differences between them (HOW). This approach will help them to make smart choices about which method will be most useful in understanding health issues or community behaviors when they arise (WHEN).
          </p>
        </motion.div>

          <motion.div
                    className="mt-6 flex items-center justify-center m-auto"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <iframe
                      width="65%"
                      height="630"
                      src="https://res.cloudinary.com/dk56u1huz/video/upload/v1740872814/Chapter_3_Types_of_Research_1_af2hce.mp4"
                      title="Introduction to Research"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    ></iframe>
                  </motion.div>

        <motion.div className='space-y-8' variants={staggerContainer}>
          {/* Observational Section with scroll in view */}
          <motion.div
            className='space-y-3'
            variants={fadeIn}
            ref={observationRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: inViewObservational ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl font-bold mb-2 text-[#005FFF] mt-10">Observational</h2>
            <ul className='list-disc list-inside'>
              {/* <li>Give examples with case studies.</li> */}
            </ul>
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="flex justify-center">
                <div className="w-64 h-40 bg-gray-300 rounded-md"></div>
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">Image placeholder</p>
            </div>
            <p>In observational research you are collecting information about something (such as people or events) without controlling the setting. This type of research may be useful when we want to understand common behaviours, health habits or trends(60-62).</p>
          </motion.div>

          {/* How Observational Research Works */}
          <motion.div className='space-y-3' variants={fadeIn}>
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">How does it work</h2>
            <p>In observational research, we simply watch, listen or look at a situation and record what we see. No changes are made to the setting for the research; the goal is to understand what is naturally happening.</p>
            <p>An observation study would involve carefully observing behaviors that we are interested in and then recording what happens (your findings). Observations are usually made at different times of day or over a period of time to get a fuller and more complete view of the behavior or routine.</p>
            <p>An important part of observational research is the focus on facts, so we record simply what we observe and not our opinions or judgments. This is called being objective.</p>
          </motion.div>

          {/* Case Study: Handwashing */}
          <motion.div
            className='lg:text-left text-center'
            variants={fadeIn}
            ref={observationRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: inViewObservational ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">Case Study: Handwashing</h2>
            <p>In one study carried out in the United Kingdom, researchers watched healthcare workers, patients, and visitors in two hospital wards to see how often they washed their hands and when. Signs were placed around the wards to let people know about the research, and no one said they did not want to be observed. The researchers spent 20 minutes on each ward observing every hour over 24 hours, taking notes on the different times where people could, or should, wash their hands. These were specific like before being in contact with patients, or after coming in contact with bodily fluids. The observers wrote down whether or not each person washed their hands. From their observations, researchers found that people washed their hands more often than had been suggested in similar research studies. They also found that out of healthcare workers, patients, and visitors, the healthcare workers washed their hands the least. (63)</p>
          </motion.div>

          {/* Experimental Section with scroll in view */}
          <motion.div
            className='space-y-3'
            variants={fadeIn}
            ref={experimentalRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: inViewExperimental ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">Experimental</h2>
            {/* <ul className='list-disc list-inside'>
              <li>Give examples with case studies.</li>
            </ul> */}
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="flex justify-center">
                <div className="w-64 h-40 bg-gray-300 rounded-md"></div>
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">Image placeholder</p>
            </div>
            <p>In observational research you are collecting information about something (such as people or events) without controlling the setting. This type of research may be useful when we want to understand common behaviours, health habits or trends(60-62).</p>
          </motion.div>

          {/* How Experimental Research Works */}
          <motion.div className='space-y-3' variants={fadeIn}>
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">How does it work</h2>
            <p>In an experiment there are usually two groups: an experiment group and a control group.</p> <p> In the experiment group, we change something, and in the control group, we do not change anything. If we wanted to test a new kind of treatment, for example, we would give the experiment group the new treatment, but not the control group.</p>
            <p>It is important that we try and make sure everything else stays the same between the two groups while we are doing the experiment – it is in this way that we can test if the new treatment makes a difference or not, because the treatment is the only thing that has been changed between the two groups.</p>
            <p>We would then look at and measure what happens in both groups: we could collect data by doing tests to see how participants responded to the treatments or ask them about their symptoms.</p> <p> Finally, we compare the results between the experiment and control groups to understand if the new treatment has worked.</p>
          </motion.div>

          {/* Case Study: Tuberculosis treatment */}
          <motion.div
            className='lg:text-left text-center'
            variants={fadeIn}
            ref={experimentalRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: inViewExperimental ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">Case Study: Tuberculosis treatment</h2>
            <p>In one study carried out in Ile Ife, Nigeria, researchers tested different ways of treating tuberculosis (TB) by separating 150 patients with TB into two groups. One group received their treatment at home, with a health worker coming by to oversee them taking their medication. The other group went to the hospital to get the same treatment. After two months, the researchers compared the two groups in different ways. They checked how many patients completed their treatment, how many patients' TB symptoms had gotten better, and how happy patients were with their treatment experience. They also looked at the cost of treatment for each group. The experiment found that patients in the home-based group were more likely to complete their treatment, were more satisfied, and had lower treatment costs than those treated in the hospital. This showed that home-based treatment may be a better, cost-effective option for TB care in the community.(67)</p>
          </motion.div>

          {/* Interventions Section with scroll in view */}
          <motion.div
            className='space-y-3'
            variants={fadeIn}
            ref={interventionRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: inViewIntervention ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">Interventions</h2>
            {/* <ul className='list-disc list-inside'>
              <li>Give examples with case studies.</li>
            </ul> */}
            <p>Doing an intervention in research is when we introduce a new program, tool, or approach into the community and study its impact. We look to see how well this new approach works to address a specific health issue. Unlike experiments, which often use controlled settings, interventions are usually carried out directly in the community, aiming for practical solutions that can improve health outcomes. (68-71)</p>
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="flex justify-center">
                <div className="w-64 h-40 rounded-md"></div>
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">Image placeholder</p>
            </div>
            <motion.div className='space-y-3' variants={fadeIn}>
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">How does it work</h2>
<p>In an intervention, a new change or solution is introduced into the community, and researchers study how it affects the problem they are trying to solve. The process often includes:</p>
            
            <p>Introducing the change: This change can take many forms; examples might be a new health education program, a practical or physical tool to help keep water clean or sanitised, or a different way of providing healthcare, such as mobile clinics.

</p>
<p>Measuring the impact: As researchers, we collect data before, during, and after the intervention to see what difference it has made. This could involve observing changes in behaviour, tracking health outcomes, or gathering feedback from community members.</p>
         <p>Improvements, changes and expanding: Based on what we learn from the process of data collection and gathering feedback laid out above, the intervention can then be improved and expanded to benefit more people or other communities.</p> 
             </motion.div>
            {/* <p>Intervention research involves putting a change or action into practice in a group, community, or system. This type of research seeks to understand the effects of the intervention on health outcomes or behaviors.</p> */}
          </motion.div>
        </motion.div>
        <div className="flex justify-between mt-10">
    <Link to="/chapter2/sectionTwo">
        <button className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-700">
            Previous 
        </button>
        </Link>
        <div className="flex justify-center ">
          <button
            onClick={() => setShowQuiz(true)}
            className="bg-white text-blue-800 border border-blue-800 px-3 py-2 rounded-lg "
          >
            Test Your Knowledge
          </button>
        </div>
        <Link to="/chapter4/video">
        <button className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-700">
            Next 
        </button>
        </Link>
    </div>
      </motion.div>
      {showQuiz && (
        <QuizPopup
          onClose={() => setShowQuiz(false)}
          onComplete={handleQuizComplete}
        />
      )}
    </div>
  );
};

export default Chapter3;
